import React from 'react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { heroImage, logoImage } from '../utils/imageOptimizer';

export const HeroSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  const primaryColor = settings.colors?.primary || '#0E7A5F';
  const primaryHover = settings.colors?.primaryHover || '#0A5F4A';

  return (
    <section id="top" className="relative min-h-[650px] overflow-hidden bg-gray-900">
      {/* Background Image */}
      {settings.heroImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage(settings.heroImage)})` }}
        />
      )}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-20 md:py-32">
        {/* Logo */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            {settings.nav?.logoImage ? (
              <img 
                src={logoImage(settings.nav.logoImage)} 
                alt={getBilingualValue(settings.nav?.logoText, 'hero.title')} 
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {(getBilingualValue(settings.nav?.logoText) || t('hero.title'))[0]}
              </div>
            )}
            <span className="text-white font-semibold text-lg">
              {getBilingualValue(settings.nav?.logoText) || t('hero.title')}
            </span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-3 mb-6">
          {(settings.heroLocation || t('hero.location')) && (
            <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium">
              {getBilingualValue(settings.heroLocation) || t('hero.location')}
            </span>
          )}
          {(settings.heroYear || t('hero.year')) && (
            <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium">
              {getBilingualValue(settings.heroYear) || t('hero.year')}
            </span>
          )}
          {(settings.heroFeature || t('hero.feature')) && (
            <span 
              className="px-4 py-2 rounded-full text-white text-sm font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              {getBilingualValue(settings.heroFeature) || t('hero.feature')}
            </span>
          )}
        </div>
        
        {/* Title */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 max-w-4xl">
          {getBilingualValue(settings.heroTitle) || t('hero.title')}
        </h1>
        
        {/* Sub-Event Themes */}
        {(settings.subEventTheme1 || settings.subEventTheme2) && (
          <div className="flex flex-wrap gap-3 mb-6">
            {settings.subEventTheme1 && (
              <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm md:text-base font-medium border border-white/30">
                {settings.subEventTheme1}
              </span>
            )}
            {settings.subEventTheme2 && (
              <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm md:text-base font-medium border border-white/30">
                {settings.subEventTheme2}
              </span>
            )}
          </div>
        )}
        
        {/* Subtitle */}
        <p className="text-lg md:text-xl lg:text-2xl text-gray-200 mb-10 max-w-2xl leading-relaxed">
          {getBilingualValue(settings.heroSubtitle) || t('hero.subtitle')}
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            size="lg"
            onClick={() => scrollToSection('exhibitor')}
            className="text-white shadow-lg text-base px-8 py-6 font-semibold"
            style={{ backgroundColor: primaryColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
          >
            {getBilingualValue(settings.heroCtaPrimary) || t('hero.ctaPrimary')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => scrollToSection('brochure')}
            className="text-white border-white hover:bg-white/10 text-base px-8 py-6 font-semibold"
          >
            {getBilingualValue(settings.heroCtaSecondary) || t('hero.ctaSecondary')}
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="mt-16 grid grid-cols-3 gap-8 max-w-xl">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white">
              {settings.stats?.exhibitors || '300+'}
            </div>
            <div className="text-gray-300 text-sm mt-1">
              {getBilingualValue(settings.stats?.exhibitorsLabel) || t('stats.exhibitors')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white">
              {settings.stats?.countries || '50+'}
            </div>
            <div className="text-gray-300 text-sm mt-1">
              {getBilingualValue(settings.stats?.countriesLabel) || t('stats.countries')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white">
              {settings.stats?.delegations || '150+'}
            </div>
            <div className="text-gray-300 text-sm mt-1">
              {getBilingualValue(settings.stats?.delegationsLabel) || t('stats.delegations')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
